import { computed, ref, watch, type Ref } from 'vue'
import {
  referenceFieldTypesFromClasses,
  type ObjectTypeFieldsResponse,
  type PassportBuilderObjectTypeFieldDto,
} from './objectTypeFields'

export type BuilderRelationRow = {
  id: number
  name: string
  toTypeId: number
  cardinality: string
  fieldId: string
}

export function useBuilderRelationRows(
  fromTypeId: Readonly<Ref<number>>,
  objectTypeFieldsResponse: Readonly<Ref<ObjectTypeFieldsResponse>>,
) {
  const rows = ref<BuilderRelationRow[]>([])
  const isLoaded = ref(false)

  async function load() {
    if (fromTypeId.value <= 0) {
      rows.value = []
      isLoaded.value = false
      return
    }

    const normalized = objectTypeFieldsResponse.value
    const referenceTypes = referenceFieldTypesFromClasses(normalized.fieldClasses)
    const referenceTypeIds = new Set(referenceTypes.map((item) => item.id))
    const typeNameById = new Map(referenceTypes.map((item) => [item.id, item.name]))

    rows.value = normalized.fields
      .filter((api: PassportBuilderObjectTypeFieldDto) =>
        referenceTypeIds.has(api.field_type) || Number(api.reference_id) > 0,
      )
      .map((api: PassportBuilderObjectTypeFieldDto) => ({
        id: api.id,
        name: api.name ?? '',
        toTypeId: Number(api.reference_id) || 0,
        cardinality: typeNameById.get(api.field_type) ?? '',
        fieldId: String(api.id),
      }))
    isLoaded.value = true
  }

  watch(
    () => [fromTypeId.value, objectTypeFieldsResponse.value] as const,
    () => load(),
    { immediate: true, deep: true },
  )

  const loading = computed(() => false)

  const relationIds = computed<Set<number> | null>(() =>
    isLoaded.value ? new Set(rows.value.map((r) => r.id)) : null,
  )

  return {
    rows,
    loading,
    isLoaded,
    relationIds,
    load,
  }
}
